import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Database, HardDrive, Image as ImageIcon, RefreshCw, Stethoscope, Wrench } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { gb, useStorageConfigQuery, useStorageHealthQuery, useStorageUsageQuery } from '@/client/api/assets/storage';
import JobLauncherDialog from '@/client/components/admin/Assets/JobLauncherDialog';
import StatsGrid, { Stat } from '@/client/components/common/StatsGrid';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorMessage } from '@/client/utils/errors';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—');

const SCAN_LABELS: Record<string, string> = {
  ok: 'Fine', missing: 'Missing', empty: 'Empty (0 bytes)', not_image: 'Not an image', uc_broken: 'Uploadcare copy broken',
  no_url: 'No address', private: 'Private (not public)', error: 'Could not reach',
};

const Card = ({ title, icon: Icon, children, className }: { title: string; icon: any; children: React.ReactNode; className?: string }) => (
  <section className={cn('rounded-lg border bg-white p-4', className)}>
    <h2 className="mb-3 flex items-center gap-2 font-semibold text-gray-900"><Icon className="h-4 w-4" /> {title}</h2>
    {children}
  </section>
);

const Row = ({ label, value, hint, to }: { label: string; value: React.ReactNode; hint?: string; to?: string }) => (
  <div className="flex items-baseline justify-between gap-3 border-b py-1.5 text-sm last:border-0">
    <span className="text-gray-600">{label}{hint && <span className="block text-[11px] text-gray-400">{hint}</span>}</span>
    {to ? <Link to={to} className="font-medium tabular-nums text-blue-600 hover:underline">{value}</Link> : <span className="font-medium tabular-nums">{value}</span>}
  </div>
);

/** Storage & usage (replaces Usage Analytics + Storage Settings). */
export const StorageSettingsPage = () => {
  useBreadcrumbs([{ label: 'Asset Management', href: '/admin/assets' }, { label: 'Storage' }]);
  const usage = useStorageUsageQuery();
  const health = useStorageHealthQuery();
  const config = useStorageConfigQuery();
  const qc = useQueryClient();
  const { hasPermission: canRun, isSuperAdmin } = useHasPermission('asset.run');
  const allowed = canRun || isSuperAdmin;
  const [job, setJob] = useState<'scan_delivery' | 'repair_uc' | 'measure' | null>(null);

  const u = usage.data;
  const h = health.data;
  const c = config.data;
  const stats: Stat[] = [
    { key: 'assets', label: 'Stored assets', value: u?.total_assets ?? 0, hint: 'not deleted, not identity media', icon: Database, tone: 'bg-blue-50 text-blue-600' },
    { key: 'bytes', label: 'Known size', value: u ? gb(u.total_bytes_known) : '—', hint: u ? `size known for ${pct(u.assets_with_size, u.total_assets)}` : '', icon: HardDrive, tone: 'bg-purple-50 text-purple-600' },
    { key: 'hashed', label: 'Hashed', value: h ? pct(h.hashed, h.images) : '—', hint: h ? `${h.hashed.toLocaleString()} of ${h.images.toLocaleString()} images` : '', icon: ImageIcon, tone: 'bg-emerald-50 text-emerald-600' },
    { key: 'vision', label: 'Vision analysed', value: h ? pct(h.vision_analysed, h.images) : '—', hint: h ? `${h.vision_analysed.toLocaleString()} images` : '', icon: Activity, tone: 'bg-amber-50 text-amber-600' },
  ];
  const weekly = u?.weekly_uploads ?? [];
  const maxWeek = Math.max(1, ...weekly.map((w) => w.assets));
  const scan = h?.last_scan;

  return (
    <div className="min-h-screen space-y-5 bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Storage</h1>
            <p className="mt-1 max-w-3xl text-sm text-gray-500">What is stored where, how it grows, what is broken, and the settings in force.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => qc.invalidateQueries({ queryKey: ['asset-storage'] })}>
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </div>

        <StatsGrid stats={stats} isLoading={usage.isLoading || health.isLoading} error={usage.isError ? getApiErrorMessage(usage.error, 'Could not load storage') : null} className="lg:grid-cols-4 xl:grid-cols-4" />

        <div className="grid gap-5 lg:grid-cols-2">
          <Card title="Health" icon={Stethoscope}>
            {!h ? <Skeleton className="h-40" /> : (
              <>
                <Row label="Live images with no Uploadcare copy" hint="the 30-minute sync should upload these" value={h.awaiting_uploadcare.toLocaleString()} />
                <Row label="Uploadcare copies awaiting removal" hint="deleted or inactive assets" value={h.uploadcare_purge_pending.toLocaleString()} />
                <Row label="Assets with no stored location" value={h.no_location.toLocaleString()} />
                <Row label="Quarantined" value={h.quarantined.toLocaleString()} to="/admin/assets?state=quarantined" />
                <Row label="Private" value={h.private.toLocaleString()} to="/admin/assets?state=private" />
                <Row label="Measured for local metrics" value={`${h.measured.toLocaleString()} (${pct(h.measured, h.images)})`} />
                {Object.entries(h.moderation).map(([s, n]) => (
                  <Row key={s} label={`Moderation: ${s}`} value={n.toLocaleString()} to={`/admin/assets?moderation=${s}`} />
                ))}
                <div className="mt-3 rounded-md bg-gray-50 p-3 text-sm">
                  <div className="mb-1 font-medium">Delivery scan</div>
                  {!scan ? <p className="text-gray-500">Never run. A scan requests every file and records what comes back.</p> : (
                    <>
                      <p className="text-xs text-gray-500">
                        Job <Link className="text-blue-600 hover:underline" to={`/admin/assets/bulk?job=${scan.ajid}`}>#{scan.ajid}</Link>
                        {' '}· {scan.status} · {scan.checked.toLocaleString()} of {scan.total.toLocaleString()} checked
                        {scan.finished_at ? ` · ${new Date(scan.finished_at).toLocaleString()}` : ''}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {Object.entries(scan.results).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                          <span key={k} className={cn('rounded-full px-2 py-0.5 text-xs', k === 'ok' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800')}>
                            {SCAN_LABELS[k] ?? k} {n.toLocaleString()}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                  {allowed && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => setJob('scan_delivery')}><Stethoscope className="mr-1.5 h-3.5 w-3.5" /> Scan delivery…</Button>
                      <Button size="sm" variant="outline" onClick={() => setJob('repair_uc')}><Wrench className="mr-1.5 h-3.5 w-3.5" /> Repair Uploadcare copies…</Button>
                      <Button size="sm" variant="outline" onClick={() => setJob('measure')}>Measure images…</Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </Card>

          <Card title="Uploads per week" icon={Activity}>
            {!u ? <Skeleton className="h-40" /> : (
              <>
                <div className="flex h-40 items-end gap-1">
                  {weekly.map((w) => (
                    <div key={w.week} className="flex-1 rounded-t bg-blue-500/80 hover:bg-blue-600" title={`Week of ${w.week}: ${w.assets.toLocaleString()} uploads, ${gb(w.bytes)}`}
                      style={{ height: `${Math.max((w.assets / maxWeek) * 100, w.assets ? 2 : 0)}%` }} />
                  ))}
                </div>
                <div className="mt-1 flex justify-between text-[11px] text-gray-400">
                  <span>{weekly[0]?.week}</span><span>last 26 weeks</span><span>{weekly[weekly.length - 1]?.week}</span>
                </div>
                <div className="mt-4 grid gap-x-6 sm:grid-cols-2">
                  <div>
                    <div className="mb-1 text-xs font-semibold uppercase text-gray-500">By type</div>
                    {u.by_kind.map((k) => <Row key={k.kind} label={k.kind} value={`${k.assets.toLocaleString()} · ${gb(k.bytes)}`} to={k.kind === 'other' ? undefined : `/admin/assets?type=${k.kind}`} />)}
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-semibold uppercase text-gray-500">By bucket</div>
                    {u.by_bucket.map((b) => <Row key={b.bucket} label={b.bucket} value={`${b.assets.toLocaleString()} · ${gb(b.bytes)}`} />)}
                    {u.by_backend.map((b) => <Row key={b.backend} label={`Backend: ${b.backend}`} value={`${b.assets.toLocaleString()} (${b.on_r2.toLocaleString()} on R2)`} />)}
                    <Row label="Deleted, still stored" value={`${u.deleted.assets.toLocaleString()} · ${gb(u.deleted.bytes)}`} to="/admin/assets?state=deleted" />
                  </div>
                </div>
              </>
            )}
          </Card>

          <Card title="Biggest owners" icon={Database}>
            {!u ? <Skeleton className="h-40" /> : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-gray-500"><tr><th className="py-1">Owner</th><th className="text-right">Assets</th><th className="text-right">Size</th></tr></thead>
                <tbody className="divide-y">
                  {u.top_owners.map((o) => {
                    const [kind, id] = o.owner.split(':');
                    return (
                      <tr key={o.owner}>
                        <td className="py-1.5">
                          <Link className="text-blue-600 hover:underline" to={`/admin/assets?${kind}=${id}`}>{o.org_name ?? (kind === 'oid' ? `Org #${id}` : `User #${id}`)}</Link>
                        </td>
                        <td className="text-right tabular-nums">{o.assets.toLocaleString()}</td>
                        <td className="text-right tabular-nums">{gb(o.bytes)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>

          <Card title="Listings over their photo limit" icon={ImageIcon}>
            {!u ? <Skeleton className="h-40" /> : !u.listings_over_base_photo_limit.length ? (
              <p className="text-sm text-gray-500">None above their type's default limit.</p>
            ) : (
              <>
                <p className="mb-2 text-xs text-gray-500">Above the default for their type. A plan or promotion can raise a listing's own limit, so check before acting.</p>
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-gray-500"><tr><th className="py-1">Listing</th><th className="text-right">Photos</th><th className="text-right">Default limit</th></tr></thead>
                  <tbody className="divide-y">
                    {u.listings_over_base_photo_limit.map((l) => (
                      <tr key={l.eid}>
                        <td className="max-w-[280px] truncate py-1.5"><Link className="text-blue-600 hover:underline" to={`/admin/assets?q=${l.eid}`}>{l.name ?? `#${l.eid}`}</Link></td>
                        <td className="text-right tabular-nums">{l.images}</td>
                        <td className="text-right tabular-nums">{l.base_limit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </Card>

          <Card title="Settings in force" icon={HardDrive} className="lg:col-span-2">
            {!c ? <Skeleton className="h-32" /> : (
              <div className="grid gap-x-8 md:grid-cols-3">
                <div>
                  <Row label="Storage backend" value={c.storage_backend ?? '—'} />
                  <Row label="Dual write (Spaces + R2)" value={c.dual_write ? 'on' : 'off'} />
                  <Row label="R2 migration" value={c.r2_migration_enabled ? 'on' : 'off'} />
                  <Row label="CDN" value={c.cdn_provider ?? '—'} />
                  <Row label="Uploadcare upload" value={c.uploadcare_upload ? 'on' : 'off'} />
                </div>
                <div>
                  <Row label="Image size cap" value={`${c.upload_caps.image_mb} MB`} />
                  <Row label="Video size cap" value={`${c.upload_caps.video_mb} MB`} />
                  <Row label="Minimum image" value={c.upload_caps.min_image_px.join(' × ')} />
                  <Row label="Minimum profile photo" value={c.upload_caps.min_profile_px.join(' × ')} />
                  {Object.entries(c.default_listing_limits).map(([k, v]) => <Row key={k} label={k.replace(/_/g, ' ')} value={String(v)} />)}
                </div>
                <div>
                  {c.schedules.map((s) => <Row key={s.name} label={s.name} hint={s.does} value={`every ${s.every_min} min`} />)}
                  <p className="mt-2 text-xs text-gray-500">These change with deploy configuration, not from here. Run a schedule now from Jobs → Scheduled.</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      <JobLauncherDialog
        open={!!job}
        onClose={() => setJob(null)}
        scope={{ filter: { state: 'active' } }}
        scopeLabel={job === 'repair_uc' ? 'every live asset (only broken Uploadcare copies change)' : 'every live asset'}
        initial={job ? { kind: 'bulk', action: job } : undefined}
      />
    </div>
  );
};
